import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LOGIN_PATH, SIGNUP_PATH } from "@/const";
import { trpc } from "@/lib/trpc";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Loader2, CreditCard, AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { toast } from "sonner";
import { usePageMeta } from "@/hooks/usePageMeta";

function formatSessionAvailableDateTime(session?: {
  examDate?: string | null;
  examStartTime?: string | null;
}, scheduleDate?: string | Date | null) {
  const sessionDate = String(session?.examDate || "").trim();
  if (sessionDate) {
    const normalizedDate = sessionDate.replace(/\//g, "-");
    const startTime = String(session?.examStartTime || "").trim();
    if (startTime) {
      const dt = new Date(`${normalizedDate}T${startTime}:00`);
      if (!Number.isNaN(dt.getTime())) {
        return dt.toLocaleString("ja-JP", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return `${sessionDate} ${startTime}`;
    }

    const d = new Date(`${normalizedDate}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
    return sessionDate;
  }

  if (scheduleDate) {
    const d = new Date(scheduleDate);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString("ja-JP", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  }

  return "未設定";
}

export default function Register() {
  usePageMeta({
    title: "受験申し込み | Biz-AI検定",
    description: "Biz-AI検定の受験申し込みページ。AssociateまたはSpecialistレベルを選択し、試験日程を確定してください。",
  });

  const { user, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [selectedSession, setSelectedSession] = useState<number | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<number | null>(null);
  const [step, setStep] = useState<"level" | "schedule" | "confirm">("level");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const { data: levels, isLoading: levelsLoading } = trpc.examLevels.list.useQuery();
  const { data: allSessions } = trpc.examSessions.list.useQuery();
  const { data: allSchedules, isLoading: schedulesLoading } = trpc.examSchedules.list.useQuery();
  const { data: myBadges } = trpc.badges.myBadges.useQuery(undefined, { enabled: !!user });
  const paymentStatusQuery = trpc.payments.status.useQuery(undefined, {
    refetchOnWindowFocus: true,
  });

  // 前提資格チェック: 選択したレベルにprerequisiteLevelIdがある場合、有効なバッジを持っているか
  const selectedLevelPrereq = levels?.find(l => l.id === selectedLevel)?.prerequisiteLevelId;
  const hasPrereqBadge = !selectedLevelPrereq || myBadges?.some(
    (b: any) => b.levelId === selectedLevelPrereq && (!b.expiresAt || new Date(b.expiresAt) > new Date())
  );
  const prereqLevelName = selectedLevelPrereq ? levels?.find(l => l.id === selectedLevelPrereq)?.displayName : null;

  const sessionsForLevel = allSessions?.filter(s => s.levelId === selectedLevel);
  const schedulesForSession = allSchedules?.filter((s: any) => s.sessionId === selectedSession);

  useEffect(() => {
    if (sessionsForLevel && sessionsForLevel.length === 1 && selectedSession !== sessionsForLevel[0].id) {
      setSelectedSession(sessionsForLevel[0].id);
    }
  }, [sessionsForLevel?.length, selectedLevel]);

  useEffect(() => {
    if (schedulesForSession && schedulesForSession.length === 1 && selectedSchedule !== schedulesForSession[0].id) {
      setSelectedSchedule(schedulesForSession[0].id);
    }
  }, [schedulesForSession?.length, selectedSession]);

  const createCheckoutSession = trpc.payments.createCheckoutSession.useMutation({
    onSuccess: (data) => {
      if (data.isFree) {
        toast.success("受験申込が完了しました");
        setLocation("/my-page");
      } else if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast.error(error.message || "申込に失敗しました");
    },
  });

  if (authLoading || levelsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-bg">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>ログインが必要です</CardTitle>
            <CardDescription>
              受験申込を行うには、ログインまたは新規登録してください。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link href={`${LOGIN_PATH}?redirect=/register`}>
              <Button className="w-full">ログイン</Button>
            </Link>
            <Link href={`${SIGNUP_PATH}?redirect=/register`}>
              <Button variant="outline" className="w-full">新規登録</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedLevelData = levels?.find(l => l.id === selectedLevel);
  const selectedSessionData = allSessions?.find(s => s.id === selectedSession);
  const selectedScheduleData = allSchedules?.find((s: any) => s.id === selectedSchedule);
  const isPaidLevel = (selectedLevelData?.price || 0) > 0;

  const handleSubmit = async () => {
    if (!selectedSession || !selectedSchedule) return;
    if (isPaidLevel) {
      const latestStatus = await paymentStatusQuery.refetch();
      if (latestStatus.data?.stripeEnabled === false) {
        toast.error("現在オンライン決済を利用できません。管理者にお問い合わせください。");
        return;
      }
    }
    createCheckoutSession.mutate({
      sessionId: selectedSession,
      scheduleId: selectedSchedule,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-bg">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between">
          <Link href="/" className="flex items-center gap-2 font-bold text-xl text-primary">
            Biz-AI
          </Link>
          <Button variant="outline" asChild>
            <Link href="/my-page">マイページ</Link>
          </Button>
        </div>
      </header>

      <main className="container py-12">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-3xl md:text-4xl font-bold">受験申込</h1>
            <p className="text-muted-foreground">
              レベルと日程を選択して、受験申込を完了してください。
            </p>
          </div>

          <div className="flex items-center justify-center gap-4">
            <div className={`flex items-center gap-2 ${step === "level" ? "text-primary" : "text-muted-foreground"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === "level" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                1
              </div>
              <span className="text-sm font-medium">レベル選択</span>
            </div>
            <div className="w-12 h-0.5 bg-border" />
            <div className={`flex items-center gap-2 ${step === "schedule" ? "text-primary" : "text-muted-foreground"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === "schedule" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                2
              </div>
              <span className="text-sm font-medium">日程選択</span>
            </div>
            <div className="w-12 h-0.5 bg-border" />
            <div className={`flex items-center gap-2 ${step === "confirm" ? "text-primary" : "text-muted-foreground"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === "confirm" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                3
              </div>
              <span className="text-sm font-medium">確認</span>
            </div>
          </div>

          {step === "level" && (
            <Card>
              <CardHeader>
                <CardTitle>レベルを選択してください</CardTitle>
                <CardDescription>
                  あなたのスキルレベルに合わせて選択してください。
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup value={selectedLevel?.toString()} onValueChange={(value) => {
                  setSelectedLevel(Number(value));
                  setSelectedSession(null);
                  setSelectedSchedule(null);
                  setAgreedToTerms(false);
                }}>
                  {levels?.map((level) => {
                    const prereqId = level.prerequisiteLevelId;
                    const hasBadge = !prereqId || myBadges?.some(
                      (b: any) => b.levelId === prereqId && (!b.expiresAt || new Date(b.expiresAt) > new Date())
                    );
                    const prereqName = prereqId ? levels?.find(l => l.id === prereqId)?.displayName : null;
                    const isDisabled = !!prereqId && !hasBadge;

                    return (
                      <div key={level.id} className={`flex items-start space-x-3 space-y-0 border rounded-lg p-4 transition-colors ${isDisabled ? "opacity-60 cursor-not-allowed bg-muted/30" : "cursor-pointer hover:bg-accent"}`}>
                        <RadioGroupItem value={level.id.toString()} id={`level-${level.id}`} disabled={isDisabled} />
                        <Label htmlFor={`level-${level.id}`} className={`flex-1 ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-lg">{level.displayName}</span>
                              <span className="text-sm text-muted-foreground">({level.name})</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{level.description}</p>
                            {isDisabled && (
                              <div className="flex items-center gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                <span>受験するには「{prereqName}」に合格している必要があります</span>
                              </div>
                            )}
                          </div>
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>
                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={() => setStep("schedule")} 
                    disabled={!selectedLevel}
                    size="lg"
                  >
                    次へ
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === "schedule" && (
            <Card>
              <CardHeader>
                <CardTitle>試験セッションと日程を選択してください</CardTitle>
                <CardDescription>
                  {selectedLevelData?.displayName}の試験日程
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {sessionsForLevel && sessionsForLevel.length > 0 && (
                  <div className="space-y-2">
                    <Label>試験セッション</Label>
                    {sessionsForLevel.length === 1 ? (
                      <p className="text-sm font-medium py-2">{sessionsForLevel[0].displayName || sessionsForLevel[0].name}</p>
                    ) : (
                      <Select value={selectedSession?.toString()} onValueChange={(value) => {
                        setSelectedSession(Number(value));
                        setSelectedSchedule(null);
                      }}>
                        <SelectTrigger>
                          <SelectValue placeholder="セッションを選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          {sessionsForLevel.map((session) => (
                            <SelectItem key={session.id} value={session.id.toString()}>
                              {session.displayName || session.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {selectedSession && (
                  <div className="space-y-2">
                    <Label>日程</Label>
                    {schedulesLoading ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      </div>
                    ) : schedulesForSession && schedulesForSession.length === 1 ? (
                      <p className="text-sm font-medium py-2">
                        {formatSessionAvailableDateTime(selectedSessionData, schedulesForSession[0].examDate)}
                      </p>
                    ) : schedulesForSession && schedulesForSession.length > 1 ? (
                      <Select value={selectedSchedule?.toString()} onValueChange={(value) => setSelectedSchedule(Number(value))}>
                        <SelectTrigger>
                          <SelectValue placeholder="日程を選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          {schedulesForSession.map((schedule: any) => (
                            <SelectItem key={schedule.id} value={schedule.id.toString()}>
                              {formatSessionAvailableDateTime(selectedSessionData, schedule.examDate)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-muted-foreground">このセッションには日程がありません。</p>
                    )}
                  </div>
                )}

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep("level")}>
                    戻る
                  </Button>
                  <Button 
                    onClick={() => setStep("confirm")} 
                    disabled={!selectedSession || !selectedSchedule}
                    size="lg"
                  >
                    次へ
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === "confirm" && (
            <Card>
              <CardHeader>
                <CardTitle>申込内容の確認</CardTitle>
                <CardDescription>
                  以下の内容で申し込みます。よろしいですか？
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">レベル</div>
                      <div className="text-sm text-muted-foreground">
                        {selectedLevelData?.displayName} ({selectedLevelData?.name})
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">試験セッション</div>
                      <div className="text-sm text-muted-foreground">
                        {selectedSessionData?.displayName || selectedSessionData?.name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">受験可能日時</div>
                      <div className="text-sm text-muted-foreground">
                        {formatSessionAvailableDateTime(selectedSessionData, selectedScheduleData?.examDate)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">受験者情報</div>
                      <div className="text-sm text-muted-foreground">
                        {user.name} ({user.email})
                      </div>
                    </div>
                  </div>
                  {selectedLevelData?.price && selectedLevelData.price > 0 ? (
                    <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                      <CreditCard className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <div className="font-medium">受験料</div>
                        <div className="text-lg font-bold text-primary">
                          ￥{selectedLevelData.price.toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          『申込を確定する』ボタンでStripeの決済ページに進みます。
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                      <div>
                        <div className="font-medium">受験料</div>
                        <div className="text-sm text-green-600 font-medium">無料</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-start gap-3 p-4 bg-muted/50 border rounded-lg">
                  <Checkbox
                    id="agree-terms"
                    checked={agreedToTerms}
                    onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                  />
                  <label htmlFor="agree-terms" className="text-sm leading-relaxed cursor-pointer">
                    <a href="/terms" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">利用規約</a>および
                    <a href="/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>に同意の上、受験を申し込みます。
                  </label>
                </div>
                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep("schedule")}>
                    戻る
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={createCheckoutSession.isPending || !agreedToTerms}
                    size="lg"
                  >
                    {createCheckoutSession.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        処理中...
                      </>
                    ) : selectedLevelData?.price && selectedLevelData.price > 0 ? (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        お支払いへ進む
                      </>
                    ) : (
                      "申込を確定する"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
